import { useState } from "react";
import Toggle from "../../components/ui/Toggle";
import { LuBell, LuMail, LuActivity, LuCreditCard, LuMegaphone } from "react-icons/lu";

export default function NotificationsTab() {
    const [prefs, setPrefs] = useState({
        scrapingComplete: true,
        creditsLow: false,
        monthlySummary: true,
        marketing: false,
    });
    
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const setPref = (key: keyof typeof prefs, value: boolean) => {
        setPrefs((p) => ({ ...p, [key]: value }));
        setHasUnsavedChanges(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 800));
        setHasUnsavedChanges(false);
        setIsSaving(false);
    };

    return (
        <div className="space-y-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                        Notification Preferences
                    </h3>
                    <p className="text-gray-500 text-sm">
                        Choose what updates you want to receive and where.
                    </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center shrink-0">
                    <LuBell className="text-violet-500" size={24} />
                </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-6">
                    <LuMail className="text-gray-400" size={18} />
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Email Notifications</h4>
                </div>

                <div className="space-y-4">
                    {/* Item 1 */}
                    <div className="flex items-start sm:items-center justify-between gap-6 p-4 rounded-2xl border border-gray-100 hover:border-violet-100 hover:bg-violet-50/30 transition-all">
                        <div className="flex gap-4 items-start">
                            <div className="mt-1 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                <LuActivity className="text-blue-500" size={16} />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Scraping Job Complete</p>
                                <p className="text-xs text-gray-500 mt-1">Get an email as soon as your extraction finishes.</p>
                            </div>
                        </div>
                        <div className="shrink-0 pt-1 sm:pt-0">
                            <Toggle
                                variant="minimal"
                                checked={prefs.scrapingComplete}
                                onChange={(v) => setPref("scrapingComplete", v)}
                            />
                        </div>
                    </div>

                    {/* Item 2 */}
                    <div className="flex items-start sm:items-center justify-between gap-6 p-4 rounded-2xl border border-gray-100 hover:border-violet-100 hover:bg-violet-50/30 transition-all">
                        <div className="flex gap-4 items-start">
                            <div className="mt-1 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                                <LuCreditCard className="text-red-500" size={16} />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Low Credit Alerts</p>
                                <p className="text-xs text-gray-500 mt-1">Receive an alert when you drop below 20% of your plan.</p>
                            </div>
                        </div>
                        <div className="shrink-0 pt-1 sm:pt-0">
                            <Toggle
                                variant="minimal"
                                checked={prefs.creditsLow}
                                onChange={(v) => setPref("creditsLow", v)}
                            />
                        </div>
                    </div>

                    {/* Item 3 */}
                    <div className="flex items-start sm:items-center justify-between gap-6 p-4 rounded-2xl border border-gray-100 hover:border-violet-100 hover:bg-violet-50/30 transition-all">
                        <div className="flex gap-4 items-start">
                            <div className="mt-1 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                                <LuBell className="text-emerald-500" size={16} />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Monthly Summary</p>
                                <p className="text-xs text-gray-500 mt-1">A monthly digest of your account activity and limits.</p>
                            </div>
                        </div>
                        <div className="shrink-0 pt-1 sm:pt-0">
                            <Toggle
                                variant="minimal"
                                checked={prefs.monthlySummary}
                                onChange={(v) => setPref("monthlySummary", v)}
                            />
                        </div>
                    </div>

                    {/* Item 4 */}
                    <div className="flex items-start sm:items-center justify-between gap-6 p-4 rounded-2xl border border-gray-100 hover:border-violet-100 hover:bg-violet-50/30 transition-all">
                        <div className="flex gap-4 items-start">
                            <div className="mt-1 w-8 h-8 rounded-full bg-fuchsia-50 flex items-center justify-center shrink-0">
                                <LuMegaphone className="text-fuchsia-500" size={16} />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Marketing & Updates</p>
                                <p className="text-xs text-gray-500 mt-1">Receive product news, tutorials, and special offers.</p>
                            </div>
                        </div>
                        <div className="shrink-0 pt-1 sm:pt-0">
                            <Toggle
                                variant="minimal"
                                checked={prefs.marketing}
                                onChange={(v) => setPref("marketing", v)}
                            />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="pt-8 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={!hasUnsavedChanges || isSaving}
                        className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gray-900 text-white shadow-lg shadow-gray-200 hover:shadow-xl disabled:opacity-50 transition-all flex items-center gap-2"
                    >
                        {isSaving ? "Saving Config..." : "Save Preferences"}
                    </button>
                </div>
            </div>
        </div>
    );
}
